import {useServerProps, useUrl} from '@shopify/hydrogen';
import clsx from 'clsx';
import {Fragment} from 'react';

import {ReactComponent as Close} from '~/assets/icons/close.svg';

import {useParamHelper} from '~/hooks/useParamHelper';

/**
 * Client component that maps Searchspring "facets" (from a fetch result)
 * to filters in the UI, which update URL params when toggled.
 * This allows for another fetch and page load to be triggered
 * in turn if/when those params change.
 * Achieved per specs of the filter object at https://docs.searchspring.com/reference/get-search
 */
export function SearchspringFacetFilters() {
  const {pathname, searchParams} = useUrl();
  const {hasNonManualParams, scrubNonManualParams} = useParamHelper();
  const {
    serverProps: {searchspringFetch},
  } = useServerProps();

  if (!searchspringFetch?.facets?.length) return null;

  const getFacetHref = (facet, facetValue) => {
    const params = new URLSearchParams(searchParams);

    // If already active, we want to toggle this param off on a link click:
    if (facetValue.active) {
      params.delete(`filter.${facet.field}.low`);
      params.delete(`filter.${facet.field}.high`);
      params.delete(`filter.${facet.field}`);
    } else {
      if (facetValue.type === 'range') {
        params.set(`filter.${facet.field}.low`, facetValue.low);
        params.set(`filter.${facet.field}.high`, facetValue.high);
      } else {
        params.set(`filter.${facet.field}`, facetValue.value);
      }
    }

    return `${pathname}?${params.toString()}`;
  };

  const clearFilters = () => {
    scrubNonManualParams();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  return (
    <div className="searchspring-facet-filters">
      {/* Show active facet toggles up top: ... */}
      <div className="searchspring-facet-filters__grouping">
        {hasNonManualParams && (
          <div className="searchspring-facet-filters__top-toggle">
            <button onClick={clearFilters}>
              <Close /> (Clear All)
            </button>
          </div>
        )}
        {searchspringFetch.facets.map((facet) => (
          <Fragment key={`active_top_toggle_group-${facet.field}`}>
            {facet.values.map((facetValue) => {
              if (!facetValue.active) return null;

              return (
                <div
                  className="searchspring-facet-filters__top-toggle"
                  key={`active_top_toggle-${facetValue.label}`}
                >
                  <a href={getFacetHref(facet, facetValue)}>
                    <Close /> {facet.label}: {facetValue.label}
                  </a>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
      {/* ... in addition to all facets and values, with active ones
          also visually cued within these lists: */}
      {searchspringFetch.facets.map((facet) => {
        // Special case(s) here, for numeric or other labels where we want clarity:
        let labelPrefix = '';

        if (facet.field === 'mfield_custom_r_value') {
          labelPrefix = 'R-value =';
        }

        return (
          <div key={facet.field}>
            <div className="searchspring-facet-filters__heading">
              {facet.label}
            </div>
            <div className="searchspring-facet-filters__grouping">
              {facet.values.map((facetValue) => {
                return (
                  <a
                    key={`${facet.field}-${facetValue.label}`}
                    className={clsx(
                      'searchspring-facet-filters__option',
                      facetValue.active &&
                        'searchspring-facet-filters__option--active',
                    )}
                    href={getFacetHref(facet, facetValue)}
                  >
                    {labelPrefix} {facetValue.label} ({facetValue.count})
                  </a>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
